import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  TellentCurrentUserDataFacade,
  TellentManageRedirectService,
  UserInvitationFacade,
} from '@core/tellent-logic';
import {
  InviteMethod,
  SeatKind,
  TellentMember,
  UserInvitationFlow,
  UserInvitationFlowData,
} from '@core/tellent-types';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogComponent,
  IconComponent,
} from '@core/user-interface-legacy';

interface MemberAddedSuccessDialogCopy {
  heading: string;
  subheading: string;
}

@Component({
  selector: 'tla-member-added-success-dialog',
  templateUrl: './member-added-success-dialog.component.html',
  styleUrls: ['./member-added-success-dialog.component.less'],
  imports: [DialogBodyComponent, ButtonComponent, TranslatePipe, IconComponent],
  providers: [TellentManageRedirectService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MemberAddedSuccessDialogComponent extends DialogComponent<UserInvitationFlowData> {
  private readonly t = injectTranslate();
  private readonly redirectService = inject(TellentManageRedirectService);
  private readonly currentUserDataFacade = inject(TellentCurrentUserDataFacade);
  private readonly userInvitationFacade = inject(UserInvitationFacade);

  public readonly orgHasGrowStandalone = this.userInvitationFacade.organizationHasGrowStandalone;
  public readonly orgHasCoreHrInactive = this.userInvitationFacade.organizationHasCoreHrInactive;

  public payload = signal<TellentMember[]>([]);
  public dialogData$ = signal<UserInvitationFlowData>({});

  public payloadContainsCoreHrSeat = computed<boolean>(() => {
    // All members added in this dialog share the same seat types, so we can just check the first one.
    const member = this.payload()[0];
    return member.seats.some(seat => seat.app.kind === SeatKind.CoreHR);
  });

  public orgHasRecruiteeOnly = computed<boolean>(() => {
    const member = this.currentUserDataFacade.organizationMembershipSafe();
    return !!(
      member &&
      member.seats.length === 1 &&
      member.seats[0].app.kind === SeatKind.Recruitment
    );
  });

  public copyObject = computed<MemberAddedSuccessDialogCopy>(() => {
    const { payload, inviteMethod, flowType } = this.dialogData$();

    if (!payload) {
      return { heading: '', subheading: '' };
    }

    const count = payload.length;
    const member: TellentMember = payload[0];
    const fullName = `${member.firstName} ${member.lastName}`;

    switch (flowType) {
      case UserInvitationFlow.AddMemberBasic:
        return {
          heading: this.t('tlc.members.member_added_success_dialog.heading.invite_via_email', {
            count,
          }),
          subheading: this.t(
            'tlc.members.member_added_success_dialog.subheading.invite_via_email',
            {
              count,
              email: member.email,
            },
          ),
        };
      case UserInvitationFlow.AddMemberAdvanced:
      case UserInvitationFlow.InviteExistingMember:
        return inviteMethod === InviteMethod.Email
          ? {
              heading: this.t('tlc.members.member_added_success_dialog.heading.invite_via_email', {
                count,
              }),
              subheading: this.t(
                'tlc.members.member_added_success_dialog.subheading.invite_via_email',
                {
                  count,
                  email: member.email,
                },
              ),
            }
          : {
              heading: this.t(
                'tlc.members.member_added_success_dialog.heading.generate_username_and_password',
                { count },
              ),
              subheading: this.t(
                'tlc.members.member_added_success_dialog.subheading.generate_username_and_password',
                { count, fullName },
              ),
            };
      case UserInvitationFlow.AddEmployeeWithoutAccess:
        return {
          heading: this.t(
            'tlc.members.member_added_success_dialog.heading.add_employees_without_access',
            { count },
          ),
          subheading: this.t(
            'tlc.members.member_added_success_dialog.subheading.add_employees_without_access',
            { count },
          ),
        };
      default:
        return { heading: '', subheading: '' };
    }
  });

  public ngOnInit(): void {
    this.payload.set(this.dialogData.payload!);
    this.dialogData$.set(this.dialogData);
  }

  public startPreonboarding(): void {
    if (!this.dialogData.payload) return;

    const memberGuids = this.dialogData.payload.map(member => member.guid);
    this.redirectService.sendMembersForPreonboarding(memberGuids);
    this.dialogRef.close({ startPreonboarding: true, completedWith: 'start_preonboarding' });
  }

  public fillOutEmployeeProfile(): void {
    if (!this.dialogData.payload) return;

    const memberGuids = this.dialogData.payload.map(member => member.guid);
    this.redirectService.fillOutEmployeeProfiles(memberGuids);
    this.dialogRef.close({ completedWith: 'fill_out_profile' });
  }

  public finishAndClose(): void {
    this.dialogRef.close({ completedWith: 'finish' });
  }

  public cancel(): void {
    this.dialogRef.close();
  }
}
